import { type CountryRelatedProduct, type CountryWithPlantDTO, type PlantWithProduct } from '@/domain/country/types';
import { useProductsBrief } from '@/domain/products/hooks';
import { type ProductBrief } from '@/domain/products/types';
import { getStatus } from '@/features/plants/utils';
import dynamic from 'next/dynamic';

const ProductDetailDialogWrapper = dynamic(
  () => import('@/features/products/components/ProductDetailDialog/ProductDetailDialogWrapper'),
);
import { groupBy } from '@/utils/array';
import { Accordion, AccordionTab } from 'primereact/accordion';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import { Box } from '@andritzot/metris-web-ui/layout/content/Box';
import { Button } from '@andritzot/metris-web-ui/inputs/button';
import { Dialog } from 'primereact/dialog';
import { Skeleton } from '@andritzot/metris-web-ui/feedback/skeleton';
import { Chip } from '@andritzot/metris-web-ui/data-display/chip';
import { Fragment, Suspense, useCallback, useState } from 'react';

interface CountryMapDetailsProps {
  isVisible: boolean;
  onClose: () => void;
  country: CountryWithPlantDTO;
}

const CountryMapDetails = ({ isVisible, onClose, country }: CountryMapDetailsProps) => {
  const { data: products } = useProductsBrief();
  const [selectedProduct, setSelectedProduct] = useState<ProductBrief | null>(null);

  const dialogFooter = (
    <div>
      <Button startIcon={<CloseOutlined />} variant="outlined" onClick={onClose}>Okay</Button>
    </div>
  );

  const handleClickProduct = (idProduct: number) => {
    const product = products?.find((p) => p.idProduct === idProduct);

    if (product) {
      setSelectedProduct(product);
    }
  };

  const onCloseProductDialog = useCallback(() => {
    setSelectedProduct(null);
  }, []);

  const renderProducts = (plant: PlantWithProduct) => {
    const grouped = groupBy(
      [...plant.products].sort((a, b) => (a.product > b.product ? 1 : -1)),
      (v) => v.status ?? -1,
    );
    return Object.keys(grouped).map((key) => (
      <Fragment key={key}>
        <span>{getStatus(Number(key))}:</span>
        <ul>
          {grouped[key].map((p: CountryRelatedProduct) => (
            <li key={p.idProduct}>
              <Button
                onClick={() => {
                  handleClickProduct(p.idProduct);
                }}
                variant="text"
                sx={{ textAlign: 'left', p: 0 }}
              >
                {p.product}
              </Button>{' '}
              {p.confidential && <Chip color="warning" size="small" label="Confidential" />}
            </li>
          ))}
        </ul>
      </Fragment>
    ));
  };

  return (
    <>
      <Dialog
        visible={isVisible}
        style={{ width: '600px' }}
        header={<span>{country.name}</span>}
        modal
        // blockScroll
        dismissableMask
        footer={dialogFooter}
        onHide={onClose}
      >
        <div className="confirmation-content">
          <div style={{ display: 'flex', flexFlow: 'column' }}>
            <Box sx={{ '& a': { textDecoration: 'none' }, '& h5': { margin: 0 } }}>
              <Accordion>
                {country.plants.map((plant) => (
                  <AccordionTab key={plant.idPlant} header={`${plant.plant}:  ${plant.products.length} products`}>
                    {renderProducts(plant)}
                  </AccordionTab>
                ))}
              </Accordion>
            </Box>
          </div>
        </div>
      </Dialog>
      {selectedProduct && (
        <Suspense fallback={<Skeleton width="100%" height="1rem" sx={{ mb: 0.25 }} />}>
          <ProductDetailDialogWrapper idProduct={selectedProduct.idProduct} onClose={onCloseProductDialog} />
        </Suspense>
      )}
    </>
  );
};

export default CountryMapDetails;
